import { useFlag } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { MinimalButton } from '@calendly/ui/components/minimal-button'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useRef, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { openFilterModal, closeFilterModal } from 'actions/meetings/filter_modal'
import { ensureFilterMenuPortalRoots, MeetingsFilterMenu } from 'components/cui_filter_menu'
import { isMeetingsTabPathPrefix } from 'hooks/home/tabs'
import { useFilters } from 'hooks/meetings/filters'
import { useScope } from 'hooks/meetings/use_scope'

import { FilterButton } from './filter_button'
import { MeetingsFilterModal } from './meetings_filter_modal'
import { changedMeetingFilters, resetParams, useIsMyCalendlyScope } from './utils'

export const MeetingsFilter = ({
  hiddenFilters = [],
  filterDefaults,
  showBuffers,
  setShowBuffers,
  useResultsCount,
  location,
  notetakerEnabled
}) => {
  const isPhone = useDeviceType('isPhone')
  const presetFlagOn = useFlag('sa_default_preset_toggle', false)
  const usePresetFilterMenuLayout = presetFlagOn && isPhone
  const [isModalOpen, openModal, closeModal] = useBooleanState(false)
  const dispatch = useDispatch()
  const isMeetingsPage = isMeetingsTabPathPrefix(window.location.pathname)
  const { applyFilterParams, getFilterParams } = useFilters()
  const filterParams = getFilterParams(filterDefaults)

  const { scopeType, scopeId } = useScope()
  const isMyCalendlyScope = useIsMyCalendlyScope()
  const changedFilters = changedMeetingFilters(isMeetingsPage, isMyCalendlyScope, filterParams)
  const filterCount = changedFilters.length
  const filterButtonRef = useRef(null)

  const scopeKey = `${scopeType}-${scopeId}`
  const previousScopeRef = useRef(scopeKey)

  const handleCloseModal = useCallback(() => {
    closeModal()
    dispatch(closeFilterModal())
  }, [closeModal, dispatch])

  const handleOpenChange = useCallback((nextIsOpen) => {
    if (nextIsOpen) {
      ensureFilterMenuPortalRoots()
      openModal()
      dispatch(openFilterModal())
    } else {
      handleCloseModal()
    }
  }, [dispatch, handleCloseModal, openModal])

  const onResetFilters = () => {
    const params = { ...resetParams(isMeetingsPage, isMyCalendlyScope, filterParams.search_keyword), ...filterDefaults }

    dispatch(applyFilterParams(params))
  }

  // Cleanup: Ensure Redux state is cleared when component unmounts
  useEffect(() => () => {
    dispatch(closeFilterModal())
  }, [dispatch])

  // Reset filters when scope changes (but not on initial mount)
  useEffect(() => {
    if (previousScopeRef.current !== scopeKey) {
      const params = { ...resetParams(isMeetingsPage, isMyCalendlyScope), ...filterDefaults }
      dispatch(applyFilterParams(params))
      previousScopeRef.current = scopeKey
    }
  }, [scopeKey, isMeetingsPage, isMyCalendlyScope, filterDefaults, dispatch, applyFilterParams])

  return (
    <>
      <MeetingsFilterMenu
        isOpen={isModalOpen}
        onOpenChange={handleOpenChange}
        onClose={handleCloseModal}
        contentMinWidth="comfortable"
        placement="bottom start"
        responsive={usePresetFilterMenuLayout}
        fullScreen={usePresetFilterMenuLayout}
      >
        <FilterButton
          ref={filterButtonRef}
          isModalOpen={isModalOpen}
          filterCount={filterCount}
          hideWhenOpen={usePresetFilterMenuLayout && isModalOpen}
        />
        {isModalOpen && (
          <MeetingsFilterModal
            hiddenFilters={hiddenFilters}
            filterDefaults={filterDefaults}
            onClose={handleCloseModal}
            showBuffers={showBuffers}
            setShowBuffers={setShowBuffers}
            useResultsCount={useResultsCount}
            location={location}
            resetParams={resetParams(isMeetingsPage, isMyCalendlyScope, filterParams.search_keyword)}
            filterParams={filterParams}
            isMyCalendlyScope={isMyCalendlyScope}
            isMeetingsPage={isMeetingsPage}
            notetakerEnabled={notetakerEnabled}
          />
        )}
      </MeetingsFilterMenu>
      {!isPhone && filterCount > 0 && (
        <MinimalButton onClick={onResetFilters} data-testid="clear-button">
          {I18n.t('publisher.scheduling.shared.empty_state.filter.button.clear')}
        </MinimalButton>
      )}
    </>
  )
}
